"use client";

import { createContext, useContext, useEffect, useState } from "react";
import { type StoreApi, useStore } from "zustand";

import { type FontKey, fontRegistry } from "@/lib/fonts/registry";
import {
  CONTENT_LAYOUT_VALUES,
  NAVBAR_STYLE_VALUES,
  SIDEBAR_COLLAPSIBLE_VALUES,
  SIDEBAR_VARIANT_VALUES,
} from "@/lib/preferences/layout";
import { THEME_PRESET_VALUES } from "@/lib/preferences/theme";
import { createPreferencesStore, type PreferencesState } from "./preferences-store";

const PreferencesStoreContext = createContext<StoreApi<PreferencesState> | null>(null);

const FONT_VALUES = Object.keys(fontRegistry) as FontKey[];

function getSafeValue<T extends string>(
  raw: string | null,
  allowed: readonly T[],
): T | undefined {
  if (!raw) return undefined;
  return allowed.includes(raw as T) ? (raw as T) : undefined;
}

export const PreferencesStoreProvider = ({
  children,
  themePreset,
  font,
  contentLayout,
  navbarStyle,
  sidebarVariant,
  sidebarCollapsible,
}: {
  children: React.ReactNode;
  themePreset: PreferencesState["themePreset"];
  font: PreferencesState["font"];
  contentLayout: PreferencesState["contentLayout"];
  navbarStyle: PreferencesState["navbarStyle"];
  sidebarVariant: PreferencesState["sidebarVariant"];
  sidebarCollapsible: PreferencesState["sidebarCollapsible"];
}) => {
  const [store] = useState(() =>
    createPreferencesStore({
      themePreset,
      font,
      contentLayout,
      navbarStyle,
      sidebarVariant,
      sidebarCollapsible,
    }),
  );

  useEffect(() => {
    const root = document.documentElement;
    const domState = {
      themePreset: getSafeValue(root.getAttribute("data-theme-preset"), THEME_PRESET_VALUES),
      font: getSafeValue(root.getAttribute("data-font"), FONT_VALUES),
      contentLayout: getSafeValue(root.getAttribute("data-content-layout"), CONTENT_LAYOUT_VALUES),
      navbarStyle: getSafeValue(root.getAttribute("data-navbar-style"), NAVBAR_STYLE_VALUES),
      sidebarVariant: getSafeValue(root.getAttribute("data-sidebar-variant"), SIDEBAR_VARIANT_VALUES),
      sidebarCollapsible: getSafeValue(root.getAttribute("data-sidebar-collapsible"), SIDEBAR_COLLAPSIBLE_VALUES),
    };
    store.setState((prev) => ({ ...prev, ...domState, isSynced: true }));
  }, [store]);

  return (
    <PreferencesStoreContext.Provider value={store}>
      {children}
    </PreferencesStoreContext.Provider>
  );
};

export const usePreferencesStore = <T,>(selector: (state: PreferencesState) => T): T => {
  const store = useContext(PreferencesStoreContext);
  if (!store) throw new Error("Missing PreferencesStoreProvider");
  return useStore(store, selector);
};
